import React, { useState } from 'react';
import { CommonContainer, TableContainer } from '../../common/Container';
import TabMenu from '../TabMenu';
import styled from 'styled-components';
import Button from '../../common/Button';
import Loading from '../../common/Loading';
import palette from '../../../lib/styles/palette';
import { FiEdit2, FiTrash2, FiFileText } from 'react-icons/fi';

const ConfigBlock = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const ToggleBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 1.5rem;
`;

const ToggleLabel = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
`;

const ToggleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  background: #f1f3f5;
  border-radius: 999px;
  padding: 4px 6px;
  border: 1px solid #dee2e6;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
  margin-left: 6px;

  input {
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + .slider {
      background-color: ${palette.webCyan[5]};

      &:before {
        transform: translateX(22px);
      }
    }
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #adb5bd;
    transition: 0.3s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
  }
`;

const ToggleStatus = styled.span`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${props => props.active ? palette.webCyan[6] : '#adb5bd'};
  margin-right: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #495057;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: ${palette.webCyan[5]};
  }
`;

const DetailBlock = styled.div`
  animation: fadeIn 0.3s ease-in;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const OptionBlock = styled.label`
  margin-bottom: 1.25rem;
  padding: 1rem 1.2rem;
  border-radius: 8px;
  border: 1px solid #e2e7ef;
  background: #fff;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: #d3dae5;
  }

  &:last-child {
    margin-bottom: 0;
  }

  input {
    margin-top: 3px;
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: ${palette.webCyan[5]};
  }
`;

const OptionLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .label-text {
    font-size: 0.95rem;
    font-weight: 600;
    color: #212529;
  }

  .description {
    margin-top: 4px;
    font-size: 0.84rem;
    line-height: 1.5;
    color: #516073;
    background: #f7f9fc;
    padding: 8px 12px;
    border-radius: 6px;
    word-break: keep-all;
  }
`;

const SelectBlock = styled.div`
  margin-top: 1rem;
  
  label {
    font-size: 0.9rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
    display: block;
  }
  
  select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    background: #fff;
    cursor: pointer;
    
    &:focus {
      outline: none;
      border-color: ${palette.webCyan[5]};
    }
  }
`;

const ListBlock = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ContractCard = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.2rem 1.4rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-bottom: 0.9rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0,0,0,0.08);
  }
`;

const ActiveBlock = styled(ContractCard)`
  border: 1px solid rgba(26, 115, 233, 0.18);
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(26, 115, 233, 0.08);
  transform: translateY(0);
  animation: slideDown 0.25s ease;
  margin-bottom: 0;
  border-left: 4px solid ${palette.webCyan[5]};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(26, 115, 233, 0.1);
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ActiveSection = styled.div`
  padding: 1.6rem 1.8rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e6ecf3;
  box-shadow: 0 8px 18px rgba(10, 31, 68, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0 2rem;
`;

const ActiveSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const ActiveHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const ActiveTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #17324d;
`;

const ActiveDescription = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: #5c6f82;
  line-height: 1.45;
`;

const EmptyActive = styled.div`
  padding: 2rem;
  border-radius: 18px;
  border: 2px dashed #d4d9df;
  background: linear-gradient(135deg, #f8f9fb 0%, #f1f3f5 100%);
  color: #2b2f36;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.7);

  .icon {
    width: 64px;
    height: 64px;
    border-radius: 20px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7a818d;
    font-size: 1.9rem;
    box-shadow: 0 10px 30px rgba(109, 117, 136, 0.15);
  }

  .title {
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #2f3238;
  }

  .description {
    font-size: 1rem;
    font-weight: 600;
    color: #4a4f58;
  }

  .emphasis {
    display: inline-block;
    margin-top: 0.25rem;
    padding: 0.4rem 0.9rem;
    border-radius: 999px;
    background: rgba(74, 79, 88, 0.08);
    color: #4a4f58;
    font-size: 0.94rem;
    font-weight: 700;
  }

  .hint {
    font-size: 0.88rem;
    color: #5f6670;
  }
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #212529;
    margin: 0;
  }
`;

const ContractInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .title {
    font-weight: 600;
    font-size: 1rem;
    color: #212529;
  }

  .meta {
    font-size: 0.85rem;
    color: #868e96;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.active ? '#fff' : '#495057'};
  background: ${props => props.active ? palette.webCyan[5] : '#e9ecef'};
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;

  button {
    padding: 6px 14px;
    font-size: 0.85rem;
  }
`;

const EContractConfig = ({
  eContractActive,
  coachActive,
  registActive,
  kioskActive,
  currentID,
  eContractList,
  loading,
  onToggleActive,
  onToggleCoachActive,
  onToggleRegistActive,
  onToggleKioskActive,
  onAddEContract,
  onModifyEContract,
  onDeleteEContract,
  onActivateContract,
}) => {
  const activeContract = eContractList?.find(contract => contract.clubEContractID === currentID) || null;
  const inactiveContracts = eContractList?.filter(contract => contract.clubEContractID !== currentID) || [];

  if (loading) {
    return (
        <>
            <TabMenu underbar />
        <ConfigBlock>
          <Loading />
        </ConfigBlock>
      </>
    );
  }

  return (
    <>
      <TabMenu underbar />
      <ConfigBlock>
        {/* 활성화/비활성화 토글 */}
        <ToggleBlock>
          <ToggleLabel>전자계약서 사용</ToggleLabel>
          <ToggleWrapper>
            <ToggleStatus active={eContractActive === 1}>
              {eContractActive === 1 ? '사용 중' : '미사용'}
            </ToggleStatus>
            <ToggleSwitch>
              <input
                type="checkbox"
                checked={eContractActive === 1}
                onChange={(e) => onToggleActive(e.target.checked ? 1 : 0)}
              />
              <span className="slider"></span>
            </ToggleSwitch>
          </ToggleWrapper>
        </ToggleBlock>

        <DetailBlock>
          {/* 강사앱 사용 */}
          <OptionBlock>
            <input
              type="checkbox"
              checked={coachActive === 1}
              onChange={(e) => onToggleCoachActive(e.target.checked ? 1 : 0)}
            />
            <OptionLabel>
              <span className="label-text">강사앱: 회원 동의서 서명 받기</span>
              <span className="description">
                강사앱에서 `마이트랩` 탭으로 이동 후 하단 [전자계약서] 버튼을 클릭하면 회원들의 회원 동의서 서명을 받을 수 있습니다.
              </span>
            </OptionLabel>
          </OptionBlock>

          {/* 회원 클럽등록시 사용 */}
          <OptionBlock>
            <input
              type="checkbox"
              checked={registActive === 1}
              onChange={(e) => onToggleRegistActive(e.target.checked ? 1 : 0)}
            />
            <OptionLabel>
              <span className="label-text">회원앱: 회원 직접 서명 동의</span>
              <span className="description">
                회원이 앱으로 클럽에 가입할 때 회원 동의서에 직접 서명합니다.
              </span>
            </OptionLabel>
          </OptionBlock>

          {/* 키오스크 사용 */}
          <OptionBlock>
            <input
              type="checkbox"
              checked={kioskActive === 1}
              onChange={(e) => onToggleKioskActive(e.target.checked ? 1 : 0)}
            />
            <OptionLabel>
              <span className="label-text">키오스크: 회원 직접 서명 동의</span>
              <span className="description">
                키오스크에서 회원이 처음 등록 시 회원 동의서에 직접 서명합니다.
              </span>
            </OptionLabel>
          </OptionBlock>

          {/* 전자계약서 리스트 */}
          <ListBlock>
            <ActiveSection>
              <ActiveSectionHeader>
                <ActiveHeading>
                  <ActiveTitle>사용 중인 전자계약서</ActiveTitle>
                  <ActiveDescription>
                    현재 회원에게 노출되는 전자계약서입니다. 필요 시 다른 계약서로 교체하거나 등록을 해제하세요.
                  </ActiveDescription>
                </ActiveHeading>
              </ActiveSectionHeader>
              {activeContract ? (
                <ActiveBlock>
                  <ContractInfo>
                    <div className="title">
                      {activeContract.title || '제목 없음'}
                      <StatusBadge active style={{marginLeft: '10px'}}>사용 중</StatusBadge>
                    </div>
                  </ContractInfo>
                  <Button
                    onClick={() => onActivateContract('')}
                    style={{padding: '6px 18px'}}
                  >
                    등록 해제
                  </Button>
                </ActiveBlock>
              ) : (
                <EmptyActive>
                  <div className="icon">
                    <FiFileText />
                  </div>
                  <div className="title">활성화된 전자계약서가 없습니다.</div>
                  <div className="description">아래 목록에서 사용할 계약서를 선택해 활성화해주세요.</div>
                  <div className="emphasis">아래 목록 → "이 전자계약서 사용하기"</div>
                  {/* <div className="hint">선택한 계약서는 즉시 회원 앱과 강사앱에 노출됩니다.</div> */}
                </EmptyActive>
              )}
            </ActiveSection>

            <div>
              <ListContainer>
                <ListHeader>
                  <SectionTitle>전자계약서 목록</SectionTitle>
                  <Button onClick={onAddEContract} cyan>
                    + 추가
                  </Button>
                </ListHeader>

                {inactiveContracts.length > 0 ? (
                  inactiveContracts.map((contract) => (
                    <ContractCard key={contract.clubEContractID}>
                      <ContractInfo>
                        <div className="title">{contract.title || '제목 없음'}</div>
                      </ContractInfo>
                      <CardActions>
                        <Button
                          cyan
                          onClick={() => onActivateContract(contract.clubEContractID)}
                        >
                          이 전자계약서 사용하기
                        </Button>
                        <IconButton variant="edit" onClick={() => onModifyEContract(contract)}>
                          <FiEdit2 />
                        </IconButton>
                        <IconButton variant="delete" onClick={() => onDeleteEContract(contract.clubEContractID)}>
                          <FiTrash2 />
                        </IconButton>
                      </CardActions>
                    </ContractCard>
                  ))
                ) : (
                  <div style={{padding: '2rem', textAlign: 'center', color: '#999'}}>
                    미사용 중인 전자계약서가 없습니다.
                  </div>
                )}
              </ListContainer>
            </div>
          </ListBlock>
        </DetailBlock>
      </ConfigBlock>
        </>
    );
};

export default EContractConfig;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.04);
  background: #f8f9fb;
  color: ${props => props.variant === 'delete' ? '#f03e3e' : palette.webCyan[5]};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);

  &:hover {
    background: ${props => props.variant === 'delete' ? '#ffe3e3' : '#e7f5ff'};
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0,0,0,0.08);
  }

  svg {
    font-size: 1.15rem;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  color: #212529;
  font-size: 1.05rem;
  font-weight: 700;
`;

const ListContainer = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.2rem;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
`;
